import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Mic, MonitorPlay, PenLine, RotateCcw, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, Textarea } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { submitBugReport, type BugReportCategory, type BugReportMode } from '@/api/bug-reports';
import { cn } from '@/lib/utils';
import { environmentContext, eventsSince, REPORTER_ATTR, snapshotHtml } from './collector';
import { canRecordScreen, RecordingError, startRecording, type ActiveRecording, type Recording } from './recorder';

/** How far back a written report reaches for clicks, requests and errors. */
const TEXT_LOOKBACK_MS = 2 * 60 * 1000;
/** A recording also carries the moments just before Start was pressed. */
const RECORDING_LOOKBACK_MS = 15 * 1000;

type Phase = 'closed' | 'compose' | 'recording' | 'review';

interface Draft {
  description: string;
  mode: BugReportMode;
  category: BugReportCategory;
  /** When the slice of the event log starts. */
  from: number;
  /** For a written report, the page as it was when the dialog opened. */
  html: string | null;
  pageUrl: string;
  recording: Recording | null;
  recordingStartedAt: number | null;
}

const BugReporterContext = createContext<{ open: () => void } | null>(null);

export function useBugReporter() {
  const value = useContext(BugReporterContext);
  if (!value) throw new Error('useBugReporter must be used inside <BugReporterProvider>.');
  return value;
}

const MODES: { value: BugReportMode; label: string; icon: typeof PenLine; needsRecorder: boolean }[] = [
  { value: 'text', label: 'Just text', icon: PenLine, needsRecorder: false },
  { value: 'screen', label: 'Record screen', icon: MonitorPlay, needsRecorder: true },
  { value: 'screen_audio', label: 'Screen + voice', icon: Mic, needsRecorder: true }
];

const CATEGORIES: { value: BugReportCategory; label: string }[] = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'ui', label: 'Something looks wrong' },
  { value: 'feature', label: 'Idea or request' },
  { value: 'question', label: 'Question' }
];

function newDraft(): Draft {
  return {
    description: '',
    mode: 'text',
    category: 'bug',
    from: Date.now() - TEXT_LOOKBACK_MS,
    html: null,
    pageUrl: location.href,
    recording: null,
    recordingStartedAt: null
  };
}

/**
 * Owns the whole report flow, mounted once in the app shell so a recording
 * keeps running while the user moves between screens:
 *
 *   compose  → (Just text) send
 *            → (Record…) the dialog gets out of the way, a floating bar shows
 *              the timer and Stop, and the user walks through the bug
 *   review   → watch it back, add words, send or record again
 */
export function BugReporterProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('closed');
  const [draft, setDraft] = useState<Draft>(newDraft);
  const active = useRef<ActiveRecording | null>(null);

  const open = useCallback(() => {
    if (active.current) return; // already recording; the bar is the way back
    // Snapshot before the dialog renders, so the page is what the user saw.
    setDraft({ ...newDraft(), html: safeSnapshot() });
    setPhase('compose');
  }, []);

  const startRecordingFlow = useCallback(async (withAudio: boolean) => {
    let recording: ActiveRecording;
    try {
      recording = await startRecording({ withAudio });
    } catch (error) {
      toast.error(error instanceof RecordingError ? error.message : 'Could not start recording.');
      return;
    }
    if (withAudio && !recording.hasAudio) {
      toast.warning('No microphone access, so this recording has no voice.');
    }
    active.current = recording;
    setDraft((d) => ({ ...d, recordingStartedAt: recording.startedAt, from: recording.startedAt - RECORDING_LOOKBACK_MS }));
    setPhase('recording');

    recording.done
      .then((result) => {
        setDraft((d) => ({ ...d, recording: result, html: safeSnapshot(), pageUrl: location.href }));
        setPhase('review');
      })
      .catch(() => {
        /* cancelled */
      })
      .finally(() => {
        active.current = null;
      });
  }, []);

  // A recording left running would keep the screen share open after sign-out
  // or a crash of the shell; stop it with the provider.
  useEffect(() => () => active.current?.cancel(), []);

  return (
    <BugReporterContext.Provider value={{ open }}>
      {children}
      {phase === 'compose' || phase === 'review' ? (
        <ReportDialog
          phase={phase}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setPhase('closed')}
          onRecord={startRecordingFlow}
        />
      ) : null}
      {phase === 'recording' && draft.recordingStartedAt ? (
        <RecordingBar
          startedAt={draft.recordingStartedAt}
          withAudio={active.current?.hasAudio ?? false}
          onStop={() => active.current?.stop()}
          onCancel={() => {
            active.current?.cancel();
            setPhase('closed');
          }}
        />
      ) : null}
    </BugReporterContext.Provider>
  );
}

function safeSnapshot(): string | null {
  try {
    return snapshotHtml();
  } catch {
    return null;
  }
}

function ReportDialog({
  phase,
  draft,
  setDraft,
  onClose,
  onRecord
}: {
  phase: 'compose' | 'review';
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
  onClose: () => void;
  onRecord: (withAudio: boolean) => void;
}) {
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const recorderAvailable = canRecordScreen();
  const recordMode = draft.mode !== 'text';
  const videoUrl = useObjectUrl(draft.recording?.blob ?? null);

  const send = async () => {
    if (!draft.description.trim() && !draft.recording) {
      toast.error('Add a sentence about what went wrong.');
      return;
    }
    setSending(true);
    setProgress(0);
    try {
      const endedAt = Date.now();
      const events = eventsSince(draft.from);
      await submitBugReport(
        {
          description: draft.description,
          mode: draft.recording ? (draft.recording.hasAudio ? 'screen_audio' : 'screen') : 'text',
          category: draft.category,
          pageUrl: draft.pageUrl,
          durationMs: draft.recording?.durationMs,
          video: draft.recording?.blob ?? null,
          html: draft.html,
          context: {
            ...environmentContext(),
            url: draft.pageUrl,
            // A recording's clock is the video's; a written report's starts at
            // its first event, so the timeline does not open on "76.2s".
            startedAt: draft.recordingStartedAt ?? events[0]?.t ?? endedAt,
            endedAt,
            events
          }
        },
        setProgress
      );
      toast.success('Thanks, your report was sent.');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send the report.');
    } finally {
      setSending(false);
    }
  };

  const footer =
    phase === 'compose' && recordMode ? (
      <>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => onRecord(draft.mode === 'screen_audio')}>
          {draft.mode === 'screen_audio' ? <Mic /> : <MonitorPlay />}
          Start recording
        </Button>
      </>
    ) : (
      <>
        {phase === 'review' ? (
          <Button
            variant="ghost"
            className="mr-auto"
            disabled={sending}
            onClick={() => {
              setDraft((d) => ({ ...d, recording: null }));
              onRecord(draft.mode === 'screen_audio');
            }}
          >
            <RotateCcw />
            Record again
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void send()} disabled={sending}>
          {sending
            ? draft.recording && progress > 0 && progress < 1
              ? `Uploading ${Math.round(progress * 100)}%`
              : 'Sending…'
            : 'Send report'}
        </Button>
      </>
    );

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !sending) onClose();
      }}
      title={phase === 'review' ? 'Send your recording' : 'Report a bug'}
      description={
        phase === 'review'
          ? 'Watch it back, add anything it doesn’t show, and send.'
          : 'Tell us what went wrong. Clicks, page details and recent errors are attached automatically.'
      }
      size={phase === 'review' ? 'lg' : 'md'}
      footer={footer}
    >
      <div {...{ [REPORTER_ATTR]: '' }} className="space-y-4">
        {phase === 'compose' ? (
          <div role="radiogroup" aria-label="How to report" className="grid grid-cols-3 gap-2">
            {MODES.map((mode) => {
              const disabled = mode.needsRecorder && !recorderAvailable;
              const selected = draft.mode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => setDraft((d) => ({ ...d, mode: mode.value }))}
                  className={cn(
                    'flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-sm transition-colors',
                    selected
                      ? 'border-[var(--accent)] bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                      : 'border-[var(--line-strong)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)]',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  <mode.icon className="size-5" />
                  {mode.label}
                </button>
              );
            })}
          </div>
        ) : null}
        {phase === 'compose' && !recorderAvailable ? (
          <p className="text-sm text-muted">This browser can’t record the screen, so text it is.</p>
        ) : null}
        {phase === 'compose' && recordMode ? (
          <p className="text-sm text-muted">
            Your browser will ask which tab to share; pick this one. Then walk through the problem
            {draft.mode === 'screen_audio' ? ' and talk us through it' : ''}, and press Stop when you’re done.
          </p>
        ) : null}

        {phase === 'review' && videoUrl ? (
          <video src={videoUrl} controls className="aspect-video w-full rounded-xl bg-black" />
        ) : null}

        <Field label="Type">
          <Select
            value={draft.category}
            onChange={(category) => setDraft((d) => ({ ...d, category: category as BugReportCategory }))}
            options={CATEGORIES}
            className="w-full"
            aria-label="Type"
          />
        </Field>
        <Field label={phase === 'compose' && recordMode ? 'Notes (optional)' : 'What happened?'}>
          <Textarea
            autoFocus={!recordMode || phase === 'review'}
            rows={4}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder="I clicked Send on an invoice and nothing happened."
          />
        </Field>
      </div>
    </Dialog>
  );
}

function useObjectUrl(blob: Blob | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return setUrl(null);
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function RecordingBar({
  startedAt,
  withAudio,
  onStop,
  onCancel
}: {
  startedAt: number;
  withAudio: boolean;
  onStop: () => void;
  onCancel: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  const clock = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div
      {...{ [REPORTER_ATTR]: '' }}
      role="status"
      className="fixed bottom-5 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3 rounded-full border border-[var(--line)] bg-[var(--surface)] py-1.5 pl-4 pr-1.5 shadow-xl"
    >
      <span className="relative flex size-2.5">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-500 opacity-60" />
        <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
      </span>
      <span className="tnum w-10 text-sm font-medium">{clock}</span>
      <span className="hidden text-sm text-muted sm:inline">
        {withAudio ? 'Recording screen and voice' : 'Recording screen'}
      </span>
      <Button size="sm" variant="ghost" aria-label="Discard recording" onClick={onCancel}>
        <X />
      </Button>
      <Button size="sm" variant="danger" className="rounded-full" onClick={onStop}>
        <Square className="fill-current" />
        Stop
      </Button>
    </div>
  );
}
